import { useEffect, useState } from "react";
import type { CrewMember } from "./useSubmissionMutations";
import type { Submission } from "./useSubmissions";
import type { TitleData } from "./useTitleMutations";

/**
 * Subtitle metadata for uploaded subtitle files
 */
export interface SubtitleMetadata {
  language: string;
  label: string;
  fileName: string;
  r2Key: string;
  /** Original format before any conversion */
  format?: "VTT" | "SRT";
  /** Processing info - optional for legacy data compatibility */
  processing?: {
    cueCount: number;
    sizeKB: number;
    wasConverted: boolean;
    wasNormalized: boolean;
    timecodeOffset: number;
  };
  /** Validation results from subtitle processing */
  validation?: {
    valid: boolean;
    errors: string[];
    /** Detailed warnings (individual cue messages) */
    warnings: string[];
    /** Grouped warnings for compact tooltip display */
    warningsSummary?: string[];
  };
}

/**
 * Unified content data interface for both submissions and published titles
 */
export interface ContentFormData {
  title: string;
  releaseYear?: number;
  runtime?: number;
  synopsis?: string;
  description?: string;
  director?: string;
  cast?: string[];
  crew?: CrewMember[];
  genres?: string[];
  rating?: string;
  language?: string | string[]; // Multi-select language array (primary usage) or single string (legacy)
  country?: string;
  posterUrl?: string;
  backdropUrl?: string;
  logoUrl?: string;
  horizontalTileUrl?: string;
  verticalTileUrl?: string;
  featureAssetId?: string;
  featureVideoUrl?: string;
  trailerAssetId?: string;
  trailerVideoUrl?: string;
  featureSubtitles?: SubtitleMetadata[];
  trailerSubtitles?: SubtitleMetadata[];
  // Submission-specific
  status?: string;
  // Title-specific
  slug?: string;
  published?: boolean;
  languages?: string[]; // Deprecated - use language field instead
  resolution?: string;
}

/**
 * Content source type - determines which API to use
 */
export type ContentMode = "submission" | "title";

/**
 * Adapter interface for content operations
 */
export interface ContentAdapter {
  mode: ContentMode;
  load: (id: string) => Promise<ContentFormData | null>;
  save: (id: string, data: Partial<ContentFormData>) => Promise<void>;
  submit?: (id: string) => Promise<void>;
  canEdit: (data: ContentFormData) => boolean;
  getTitle: (data: ContentFormData) => string;
}

/**
 * Normalize submission data to ContentFormData
 */
export function normalizeSubmission(submission: Submission): ContentFormData {
  // Handle directorData - can be either array or single object
  const directorDataRaw = submission.directorData as { name: string } | Array<{ name: string }> | null;
  const directorData = Array.isArray(directorDataRaw) ? directorDataRaw[0] : directorDataRaw;

  const castData = submission.castData as Array<{ name: string }> | null;
  const crewData = submission.crewData as CrewMember[] | null;
  const genreData = submission.genreData as string[] | null;

  const featureAsset = submission.assets?.find((a) => a.kind === "FEATURE");
  const trailerAsset = submission.assets?.find((a) => a.kind === "TRAILER");

  // Extract subtitle metadata from submission.metadata JSON

  const metadata = submission.metadata as {
    featureSubtitles?: SubtitleMetadata[];
    trailerSubtitles?: SubtitleMetadata[];
  } | null;
  const featureSubtitles = metadata?.featureSubtitles || submission.featureSubtitles;
  const trailerSubtitles = metadata?.trailerSubtitles || submission.trailerSubtitles;

  const normalized: ContentFormData = {
    title: submission.title,
    releaseYear: submission.year || undefined,
    runtime: submission.durationMinutes || undefined,
    synopsis: submission.synopsis || undefined,
    description: submission.description || undefined,
    director: directorData?.name || undefined,
    cast: castData?.map((c) => c.name) || undefined,
    crew: crewData || undefined,
    genres: genreData || undefined,
    rating: submission.rating || undefined,
    languages: submission.languages || [],
    language: submission.languages?.[0] || undefined, // Backward compat
    country: submission.country || undefined,
    posterUrl: submission.posterUrl || undefined,
    backdropUrl: submission.backdropUrl || undefined,
    logoUrl: submission.logoUrl || undefined,
    horizontalTileUrl: submission.horizontalTileUrl || undefined,
    verticalTileUrl: submission.verticalTileUrl || undefined,
    featureAssetId: featureAsset?.id,
    featureVideoUrl: featureAsset?.id ? `/placeholder-video-${featureAsset.id}` : undefined,
    trailerAssetId: trailerAsset?.id,
    trailerVideoUrl: trailerAsset?.id ? `/placeholder-video-${trailerAsset.id}` : undefined,
    featureSubtitles,
    trailerSubtitles,
    status: submission.status,
  };

  return normalized;
}

/**
 * Normalize title data to ContentFormData
 */
export function normalizeTitle(title: TitleData): ContentFormData {
  const castData = title.castData as Array<{ name: string }> | null;
  const crewData = title.crewData as CrewMember[] | null;

  const featureAsset = title.assets?.find((a) => a.kind === "FEATURE");
  const trailerAsset = title.assets?.find((a) => a.kind === "TRAILER");

  // For assets with encodingStatus PENDING/UPLOADED but no hlsUrl yet,
  // use a placeholder to indicate upload is complete but encoding pending
  const getVideoUrl = (asset: typeof featureAsset) => {
    console.log("[normalizeTitle] getVideoUrl called:", asset);
    if (!asset) return undefined;

    // Check if hlsUrl has actual content (not empty string)
    if (asset.hlsUrl && asset.hlsUrl.trim() !== "") {
      return asset.hlsUrl;
    }

    // If asset exists but hlsUrl is empty/null, check encoding status
    if (asset.encodingStatus === "PENDING" || asset.encodingStatus === "UPLOADED") {
      return `pending:${asset.id}`;
    }

    return undefined;
  };

  const normalized = {
    title: title.name,
    releaseYear: title.year || undefined,
    runtime: title.runtimeMinutes || undefined,
    synopsis: title.synopsis || undefined,
    director: title.directorName || undefined,
    cast: castData?.map((c) => c.name) || title.castNames || undefined,
    crew: crewData || undefined,
    genres: title.genres || undefined,
    country: title.country || undefined,
    posterUrl: title.posterUrlHd || undefined,
    backdropUrl: title.backdropUrlHd || undefined,
    logoUrl: title.logoUrl || undefined,
    horizontalTileUrl: title.horizontalTileUrl || undefined,
    verticalTileUrl: title.verticalTileUrl || undefined,
    featureAssetId: featureAsset?.id,
    featureVideoUrl: getVideoUrl(featureAsset),
    trailerAssetId: trailerAsset?.id,
    trailerVideoUrl: getVideoUrl(trailerAsset),
    slug: title.slug,
    published: title.published,
    languages: title.languages || undefined,
    resolution: title.resolution || undefined,
  };

  console.log("[normalizeTitle] result:", {
    featureAsset,
    trailerAsset,
    featureVideoUrl: normalized.featureVideoUrl,
    trailerVideoUrl: normalized.trailerVideoUrl,
  });

  return normalized;
}

/**
 * Hook for managing content editor state (works for both submissions and titles)
 */
export function useContentEditor(adapter: ContentAdapter, id: string | undefined) {
  const [formData, setFormData] = useState<ContentFormData>({ title: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadContent() {
      if (!id) {
        setError("No ID provided");
        setLoading(false);
        return;
      }

      try {
        const data = await adapter.load(id);
        if (data) {
          setFormData(data);
        } else {
          setError("Content not found");
        }
      } catch (err) {
        console.error("Failed to load content:", err);
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    }

    void loadContent();
  }, [id, adapter]);

  const handleFieldChange = (field: keyof ContentFormData, value: string | number | string[] | CrewMember[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handleVideoUpload = (type: "feature" | "trailer", assetId: string, videoUrl: string) => {
    if (type === "feature") {
      setFormData((prev) => ({
        ...prev,
        featureAssetId: assetId,
        featureVideoUrl: videoUrl,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        trailerAssetId: assetId,
        trailerVideoUrl: videoUrl,
      }));
    }
  };

  const handleSubtitleChange = (type: "feature" | "trailer", subtitles: SubtitleMetadata[]) => {
    if (type === "feature") {
      setFormData((prev) => ({
        ...prev,
        featureSubtitles: subtitles,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        trailerSubtitles: subtitles,
      }));
    }
  };

  const saveChanges = async () => {
    if (!id) return;
    try {
      await adapter.save(id, formData);
    } catch (err) {
      console.error("Failed to save changes:", err);
      throw err;
    }
  };

  return {
    formData,
    loading,
    error,
    handleFieldChange,
    handleVideoUpload,
    handleSubtitleChange,
    saveChanges,
    canEdit: adapter.canEdit(formData),
  };
}
